// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { forkJoin, Subscription } from "rxjs";

// Custom packages
import { DocumentArchiveExportDialogData } from "src/app/modules/properties/components/document-archive-export-ras-dialog/document-archive-export-ras-dialog.component";
import { ExportContractsDialogComponent } from "src/app/modules/properties/components/export-contracts-dialog/export-contracts-dialog.component";
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import {
  documentArchiveContractItems,
  documentArchiveExportLoadErrorKey,
} from "src/app/modules/properties/document-archive-export.utils";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { isArchiveObjectId } from "src/app/modules/search-docs/document-archive-general-search.utils";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { ApiResponse } from "src/app/shared/interfaces/_base/api-response.interface";
import { DocumentArchiveTree } from "src/app/shared/interfaces/document-archive-doc.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * «Esporta contratti» of the dossier on the 3.0.0 archive (FE-M8).
 *
 * The legacy dialog with its data source swapped: it EXTENDS `ExportContractsDialogComponent` and only
 * replaces how the contracts are read, so the Word file is written by the very `onExportFile` of the
 * legacy. The legacy dialog stays as it is for the legacy dossier until the switch.
 *
 * What changes is where the contracts come from:
 *  - `GET /document-archive-docs/tree` instead of `document-archives/:id/contracts`, with the rules of
 *    @see documentArchiveContractItems;
 *  - no `document-archives/settings` and no section computed from the retired `key`: the QR code of a
 *    contract is `/qrcode/d/{_id of the row}` (NEW-A3);
 *  - no longer limited to the properties with a mapping: the tree answers for every archive;
 *  - a failure says so, instead of the legacy "no contract to export".
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-document-archive-export-contracts-dialog",
  templateUrl: "./document-archive-export-contracts-dialog.component.html",
})
export class DocumentArchiveExportContractsDialogComponent
  extends ExportContractsDialogComponent
  implements OnInit, OnDestroy
{
  /** Translation key of the message shown when the contracts cannot be read, `""` when they could */
  loadErrorKey = "";

  /** The two reads of the dialog, cancelled when it closes */
  private loadSubscription: Subscription;
  /** The services the legacy dialog keeps private, under names of their own */
  private readonly cookies: CookieService;
  private readonly properties: PropertiesService;

  /**
   * Class constructor: the dependencies of the legacy dialog, and the service of the new archive
   */
  constructor(
    dialogRef: MatDialogRef<ExportContractsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: DocumentArchiveExportDialogData,
    configService: ConfigService,
    cookieService: CookieService,
    translateService: TranslateService,
    propertiesService: PropertiesService,
    helperService: HelperService,
    documentArchivesService: DocumentArchivesService,
    settingsService: SettingsService,
    private documentArchiveDocsService: DocumentArchiveDocsService
  ) {
    super(
      dialogRef,
      data,
      configService,
      cookieService,
      translateService,
      propertiesService,
      helperService,
      documentArchivesService,
      settingsService
    );
    this.cookies = cookieService;
    this.properties = propertiesService;
  }

  /**
   * Read the property, for the title of the file, and the tree, together. Replaces the legacy
   * `ngOnInit` entirely: none of its calls is made.
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.lang = this.cookies.get("lang") || this.configService.defaultLang;

    const propertyId = this.data ? this.data.propertyId : undefined;
    if (!isArchiveObjectId(propertyId)) {
      this.dialogIsLoading = false;
      this.loadErrorKey = documentArchiveExportLoadErrorKey(null);
      return;
    }

    this.loadSubscription = forkJoin([
      this.properties.get(propertyId),
      this.documentArchiveDocsService.getTreeByProperty(propertyId),
    ]).subscribe(
      (results: [any, ApiResponse<DocumentArchiveTree>]) => {
        this.dialogIsLoading = false;
        const propertyRes = results[0];
        // ApiService emits a network failure of the legacy route as a VALUE
        if (propertyRes instanceof Error) {
          this.loadErrorKey = documentArchiveExportLoadErrorKey(propertyRes);
          return;
        }
        const property = propertyRes && propertyRes.status && propertyRes.data ? propertyRes.data : null;
        const tree = results[1] && results[1].data ? results[1].data : null;
        if (!property || !tree) {
          this.loadErrorKey = documentArchiveExportLoadErrorKey(null);
          return;
        }

        this.property.setData(property);
        this.propertyData = property;
        this.propertyName = typeof property.name === "string" ? property.name : "";
        this.documentArchive = documentArchiveContractItems(
          tree.area,
          this.lang,
          this.configService.frontEndHost
        );
      },
      (err: unknown) => {
        this.dialogIsLoading = false;
        this.loadErrorKey = documentArchiveExportLoadErrorKey(err);
      }
    );
  }

  /**
   * Destroy component
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    if (this.loadSubscription) {
      this.loadSubscription.unsubscribe();
    }
  }
}

/**
 * Open «Esporta contratti» of the 3.0.0 archive for a property, at the size and with the closing rule
 * of the legacy dialog: how the legacy pages that keep the export open it once the switch is live.
 *
 * @since 3.0.0
 *
 * @param dialog     The MatDialog of the page
 * @param propertyId The property
 */
export function openDocumentArchiveExportContractsDialog(
  dialog: MatDialog,
  propertyId: string
): MatDialogRef<DocumentArchiveExportContractsDialogComponent> {
  const data: DocumentArchiveExportDialogData = { propertyId };
  return dialog.open(DocumentArchiveExportContractsDialogComponent, {
    minWidth: "50%",
    maxHeight: "98vh",
    data,
    disableClose: true,
  });
}
